import { FC, useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { adminLoginUserDataAtom } from '@recoil_iqfu/atoms/auth/adminLoginUserData';
import { companyLoginUserDataAtom, companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';
import {
  FinancialAgencyServices,
  finanialAgencyLoginUserDataAtom,
} from '@recoil_iqfu/atoms/auth/financialAgencyLoginUserData';
import { isLoggedInAtom } from '@recoil_iqfu/atoms/auth/isLoggedIn';

import {
  adminRoleBaseRestrictedRoutes,
  FinancialAgencyPermittedRoutes,
  OutsourcerPermittedRoutes,
  UserType,
} from '@constants_common';
import { useAuth } from '@contexts_common/authContext';

type Props = {
  userType: UserType;
};

const ProtectedRoutes: FC<Props> = ({ userType, children }) => {
  const router = useRouter();
  const [renderPage, setRenderPage] = useState<boolean>(false);
  const isBrowser = () => typeof window !== 'undefined';

  const [adminLoginUserData, setAdminLoginUserData] = useRecoilState(adminLoginUserDataAtom);
  const [companyLoginUserData, setCompanyLoginUserData] = useRecoilState(companyLoginUserDataAtom);
  const [loginFinancialAgencyUserData, setLoginFinancialAgencyUserData] = useRecoilState(
    finanialAgencyLoginUserDataAtom,
  );

  const { isAuthenticated } = useAuth();

  const setIsLoggedIn = useSetRecoilState(isLoggedInAtom);

  const { isMultipleCompanyUser } = useRecoilValue(companyLoginUserDataSelector);

  const adminUserRole = adminLoginUserData?.role;
  const companyServiceList = companyLoginUserData?.services;
  const financialAgencyServiceList = loginFinancialAgencyUserData?.services;

  const pathIsProtected = router.pathname.indexOf('console') !== -1;

  const isInRestrictedPath = () => {
    const isInWrongUserPage =
      (userType === 'admin' && !router.pathname.includes('console/admin')) ||
      (userType === 'company' && !router.pathname.includes('console/company')) ||
      (userType === 'financial_agency' && !router.pathname.includes('console/financial_agency'));

    //adminのmanagerとmemberの許可しないurlにいるかの判定
    //outsourcerは別箇所で判定する
    const isInAdminRoleBaseRestrictedPath =
      userType === 'admin' &&
      adminUserRole &&
      adminUserRole !== 'outsourcer' &&
      adminRoleBaseRestrictedRoutes[adminUserRole].includes(router.pathname);

    //adminのoutsourcerの許可しないurlにいるかの判定
    //※permission_codesの許可するURLのどれにも一致しない場合はtrue
    const isInOutsourserCodeBaseRestrictedPath =
      userType === 'admin' &&
      adminUserRole === 'outsourcer' &&
      adminLoginUserData?.permission_codes
        ?.map((code) => {
          return OutsourcerPermittedRoutes[code].includes(router.pathname);
        })
        .includes(true) === false;

    //companyユーザーのiqfu未加入会社ユーザーがiqfu機能にアクセスしているかの判定
    //※イクラウドでもなくイクフプロでないページにアクセスするとtrue
    const isInIqfuRestrictedPath =
      userType === 'company' &&
      !companyServiceList?.includes('iqfu') &&
      router.pathname !== '/console/company/dashboard' &&
      !router.pathname.startsWith('/console/company/iqfu_pro') &&
      !router.pathname.startsWith('/console/company/iqra_cloud');

    //companyユーザーでイクラウド未加入ユーザーがイクラウドへアクセスしているかの判定
    const isInIqloudRestrictedPath =
      userType === 'company' &&
      !companyServiceList?.includes('cloud') &&
      router.pathname.startsWith('/console/company/iqra_cloud');

    //companyユーザーでイクフプロ未加入ユーザーがイクフプロへアクセスしているかの判定
    const isInIqfuProRestrictedPath =
      userType === 'company' &&
      !companyServiceList?.includes('pro') &&
      router.pathname.startsWith('/console/company/iqfu_pro');

    //複数会社ユーザーは重説アップロード画面に遷移させない
    const isInRestrictedJusetsuUploadPath =
      userType === 'company' &&
      isMultipleCompanyUser &&
      router.pathname === '/console/company/iqra_cloud/jusetsu/upload';

    const financialAgencyUnavailableServiceList: FinancialAgencyServices[] = (
      ['leaseback', 'refer_seller', 'pro'] as FinancialAgencyServices[]
    ).filter((service) => !financialAgencyServiceList?.includes(service));

    const isInRestrictedFinancialAgencyPath =
      userType === 'financial_agency' &&
      financialAgencyUnavailableServiceList
        .map((service) => {
          const unavailablePathList = FinancialAgencyPermittedRoutes[service];
          const isInUnavailablePath = unavailablePathList.some((path) => router.pathname.startsWith(path));
          return isInUnavailablePath;
        })
        .includes(true);

    return (
      isInWrongUserPage ||
      isInAdminRoleBaseRestrictedPath ||
      isInOutsourserCodeBaseRestrictedPath ||
      isInIqfuRestrictedPath ||
      isInIqloudRestrictedPath ||
      isInIqfuProRestrictedPath ||
      isInRestrictedJusetsuUploadPath ||
      isInRestrictedFinancialAgencyPath
    );
  };

  useEffect(() => {
    //動的パスの部分がちゃんとrouter.asPathにセットされてから処理を動かす
    if (router.isReady) {
      (async () => {
        //router.asPathの / を - に変換する
        //最初の / は削除する
        // router.asPathの === / のときは 'top' にする
        // ?以後のquery parameterは落とす
        const pathParam = router.asPath === '/' ? 'top' : router.asPath.slice(1).replace(/\//g, '-').split('?')[0];

        const authValidity = await isAuthenticated({ userType: userType, pagePath: pathParam });

        setIsLoggedIn(authValidity);

        if ((isBrowser() && !authValidity && pathIsProtected) || isInRestrictedPath()) {
          // console/${userType}　のパス以外のパスにいる場合は、無限ループになる可能性があるため、ログイン後のリダイレクト設定はしない
          const shouldRedirect = router.pathname.includes(`console/${userType}`);

          //hasRedirectedがtrueの場合は、リダイレクトの無限ループを防ぐため、ログイン後のリダイレクト設定はしない
          const redirectUrl =
            shouldRedirect && router.query.hasRedirected !== 'true' ? encodeURIComponent(router.asPath) : undefined;

          // ログイン後のリダイレクト先のパスを生成する
          const loginPath = `/console/${userType}/login${redirectUrl ? `?redirect=${redirectUrl}` : ''}`;

          router.replace(loginPath);
        } else {
          setRenderPage(true);
        }
      })();
    }
  }, [router, userType, adminUserRole]);

  return renderPage ? <>{children}</> : <></>;
};

export default ProtectedRoutes;
